import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  DialogActions,
  DialogContent,
  List,
  ListItemAvatar,
  ListItemText,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';
import Autocomplete from 'components/Autocomplete';
import ContentLoader from 'components/ContentLoader';
import { DialogTitle } from 'components/Dialog';
import { URL_MY_SCORECARDS } from 'constants/urls';
import { commonTexts, reviewCyclesTexts } from 'i18n';
import { resizeImage } from 'helpers';
import { useAppDispatch } from 'store';
import { ScorecardListItem } from 'store/interfaces';
import { selectAllReviewCycles, updateOptions } from 'store/options';
import { UIOption } from 'store/options/interfaces/UIOption';
import { changeFilter, createScorecard } from 'store/scorecards';
import { storeName } from 'store/scorecards/own/config';
import fetchOwnScorecards from './fetchOwnScorecards';
import messages from './messages';
import {
  CloneListMetaDivider,
  CloneListMetaRow,
  CloneListMeta,
  CloneListText,
  CloneListTitle,
  CloneNote,
  ListItem,
  MethodHint,
  MethodLabel,
  MethodOption,
  MethodTitle,
  RequiredAsterisk,
  ScorecardsList,
  Section,
  SectionHeader,
  StyledDialog,
} from './CreateScorecardDialog.styled';

type CreationMethod = 'scratch' | 'clone';

const METHOD_OPTIONS: Array<{
  value: CreationMethod;
  title: MessageDescriptor;
  hint: MessageDescriptor;
}> = [
  {
    value: 'scratch',
    title: messages.fromScratch,
    hint: messages.fromScratchHint,
  },
  {
    value: 'clone',
    title: messages.cloneFromExisting,
    hint: messages.cloneFromExistingHint,
  },
];

interface CreateScorecardDialogProps {
  onClose: () => void;
}

export default function CreateScorecardDialog({
  onClose,
}: CreateScorecardDialogProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { formatDate, formatMessage } = useIntl();
  const reviewCycles = useSelector(selectAllReviewCycles);

  const [selectedCycle, setSelectedCycle] = useState<UIOption | null>(null);
  const [method, setMethod] = useState<CreationMethod | null>(null);
  const [ownScorecards, setOwnScorecards] = useState<
    ScorecardListItem[] | null
  >(null);
  const [selectedSource, setSelectedSource] =
    useState<ScorecardListItem | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (reviewCycles.length === 0) {
      dispatch(updateOptions('reviewCycles'));
    }
  }, [dispatch, reviewCycles.length]);

  useEffect(() => {
    fetchOwnScorecards().then(setOwnScorecards);
  }, []);

  const sortedSources = useMemo(
    () =>
      [...(ownScorecards ?? [])].sort((a, b) =>
        (b.reviewCycle?.endAt ?? '').localeCompare(a.reviewCycle?.endAt ?? ''),
      ),
    [ownScorecards],
  );

  const isLoadingCycles = reviewCycles.length === 0;

  const onCycleChange = (e: SyntheticEvent, option: UIOption | null) =>
    setSelectedCycle(option);

  const onMethodSelect = (nextMethod: CreationMethod) => {
    setMethod(nextMethod);

    if (nextMethod === 'scratch') {
      setSelectedSource(null);
    }
  };

  const canSubmit =
    Boolean(selectedCycle) &&
    (method === 'scratch' || (method === 'clone' && Boolean(selectedSource)));

  const getLastUpdatedText = (scorecard: ScorecardListItem) => {
    if (!scorecard.lastUpdatedAt) {
      return null;
    }

    return formatMessage(
      {
        id: 'pages.myTeam.lastUpdated',
        defaultMessage: 'Last updated: {date}{by}',
      },
      {
        date: `${formatDate(scorecard.lastUpdatedAt, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })} at ${formatDate(scorecard.lastUpdatedAt, {
          hour: 'numeric',
          minute: 'numeric',
        })}`,
        by: '',
      },
    );
  };

  const handleCreate = () => {
    if (!canSubmit || !selectedCycle) return;

    const reviewCycleId = Number(selectedCycle.id);
    setSubmitting(true);

    dispatch(
      createScorecard({
        reviewCycleId,
        ...(selectedSource ? { sourceScorecardId: selectedSource.id } : {}),
      }),
    )
      .unwrap()
      .then((scorecard) => {
        dispatch(
          changeFilter({
            store: storeName,
            data: { key: 'reviewCycleIds', value: reviewCycleId },
          }),
        );

        onClose();
        navigate(
          generatePath(`/${URL_MY_SCORECARDS}/:scorecardId`, {
            scorecardId: String(scorecard.id),
          }),
        );
      })
      .catch(() => setSubmitting(false));
  };

  return (
    <StyledDialog open onClose={onClose} fullWidth>
      <DialogTitle onClose={onClose}>
        <FormattedMessage {...messages.title} />
      </DialogTitle>
      <DialogContent>
        <Section>
          <SectionHeader variant="body4">
            <FormattedMessage {...messages.selectReviewCycle} />
            <RequiredAsterisk aria-hidden="true">*</RequiredAsterisk>
          </SectionHeader>
          <Autocomplete
            size="large"
            isInModal
            disabled={isLoadingCycles}
            options={reviewCycles}
            value={selectedCycle}
            onChange={onCycleChange}
            placeholder={formatMessage(reviewCyclesTexts.singleTitle)}
          />
        </Section>

        <Section>
          <SectionHeader variant="body4">
            <FormattedMessage {...messages.creationMethod} />
          </SectionHeader>
          <RadioGroup
            value={method ?? ''}
            onChange={(e) => onMethodSelect(e.target.value as CreationMethod)}
          >
            {METHOD_OPTIONS.map(({ value, title, hint }) => (
              <MethodOption
                key={value}
                value={value}
                control={<Radio size="small" />}
                disableTypography
                label={
                  <MethodLabel>
                    <MethodTitle variant="body4">
                      <FormattedMessage {...title} />
                    </MethodTitle>
                    <MethodHint variant="body6">
                      <FormattedMessage {...hint} />
                    </MethodHint>
                  </MethodLabel>
                }
              />
            ))}
          </RadioGroup>
        </Section>

        {method === 'clone' && (
          <Section>
            {ownScorecards === null && <ContentLoader />}
            {ownScorecards !== null && sortedSources.length === 0 && (
              <Typography variant="body2">
                <FormattedMessage {...messages.cloneEmptyState} />
              </Typography>
            )}
            {sortedSources.length > 0 && (
              <>
                <SectionHeader variant="body4">
                  <FormattedMessage {...messages.scorecardsList} />
                </SectionHeader>
                <CloneNote variant="body6">
                  <FormattedMessage {...messages.cloneNote} />
                </CloneNote>
                <ScorecardsList>
                  <List>
                    {sortedSources.map((scorecard) => (
                      <ListItem
                        key={scorecard.id}
                        isSelected={selectedSource?.id === scorecard.id}
                        onClick={() => setSelectedSource(scorecard)}
                      >
                        <ListItemAvatar>
                          <Avatar
                            alt={scorecard?.workRecord?.user?.fullName || ''}
                            src={resizeImage(
                              scorecard?.workRecord?.user?.avatar || null,
                            )}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          disableTypography
                          primary={
                            <CloneListText>
                              <CloneListTitle variant="body4">
                                {scorecard.name}
                              </CloneListTitle>
                              <CloneListMetaRow>
                                <CloneListMeta variant="body6">
                                  {scorecard.reviewCycle?.name}
                                </CloneListMeta>
                                {getLastUpdatedText(scorecard) && (
                                  <>
                                    <CloneListMetaDivider>
                                      |
                                    </CloneListMetaDivider>
                                    <CloneListMeta variant="body6">
                                      {getLastUpdatedText(scorecard)}
                                    </CloneListMeta>
                                  </>
                                )}
                              </CloneListMetaRow>
                            </CloneListText>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </ScorecardsList>
              </>
            )}
          </Section>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          <FormattedMessage {...commonTexts.cancel} />
        </Button>
        <LoadingButton
          variant="contained"
          disabled={!canSubmit}
          loading={isSubmitting}
          onClick={handleCreate}
        >
          <FormattedMessage {...messages.create} />
        </LoadingButton>
      </DialogActions>
    </StyledDialog>
  );
}
